import { apiClient } from "@/lib/apiClient";
import i18n from "@/utils/i18n";
import type {
  Education,
  CreateEducationRequest,
  AdminCreateEducationDto,
  AdminPatchEducationDto,
  EducationFilters,
  EducationListItemApi,
  EducationListParams,
  EducationListResponseApi,
  EducationDetailApi,
  EducationDetailResponseApi,
  MandatoryHealthResponse,
  AttendanceListResponseApi,
  GetAttendanceParams,
  PutAttendanceBody,
  PresetLocationsResponseApi,
} from "../types/education-management.types";
import {
  EDUCATION_TYPE_API,
  EDUCATION_STATUS_API,
} from "../types/education-management.types";
import type {
  EducationStatus,
  EducationFormat,
} from "../types/education-management.types";
import type {
  CompleteEducationCandidatesResponse,
  CompleteEducationRequest,
  CompleteEducationResponse,
} from "../types/complete-education.types";

const ADMIN_EDUCATIONS_ENDPOINT = "/api/admin/educations";
const EDUCATIONS_ATTENDANCE_ENDPOINT = "/api/admin/educations";
const EDUCATION_CATEGORIES_ENDPOINT = "/api/client/education-categories";
const USERS_INSTRUCTORS_ENDPOINT = "/api/users/instructors";
const ADMIN_LOCATIONS_ENDPOINT = "/api/admin/locations";

/** Map API type (0-3) to UI format (exported for detail drawer) */
export function apiTypeToFormat(type: number): EducationFormat {
  switch (type) {
    case EDUCATION_TYPE_API.ON_SITE:
      return "yuzYuze";
    case EDUCATION_TYPE_API.ONLINE:
      return "canli";
    case EDUCATION_TYPE_API.UNIT:
      return "uniteli";
    case EDUCATION_TYPE_API.NONUNIT:
      return "nonUnit";
    default:
      return "uniteli";
  }
}

/** Map API status (0-5) to UI status (exported for detail drawer) */
export function apiStatusToStatus(status: number): EducationStatus {
  switch (status) {
    case EDUCATION_STATUS_API.REQUEST_COLLECTION:
      return "talepToplama";
    case EDUCATION_STATUS_API.BUDGET_PENDING:
      return "butceBekliyor";
    case EDUCATION_STATUS_API.ACTIVE:
      return "aktif";
    case EDUCATION_STATUS_API.CLOSED:
      return "kapali";
    case EDUCATION_STATUS_API.ARCHIVED:
      return "tamamlandi";
    case EDUCATION_STATUS_API.DRAFT:
      return "taslak";
    default:
      return "aktif";
  }
}

function mapApiItemToEducation(item: EducationListItemApi): Education {
  const instructors = item.instructors ?? [];
  const educatorName =
    instructors.length > 0
      ? (([instructors[0].firstName, instructors[0].lastName]
          .filter(Boolean)
          .join(" ") ||
          instructors[0].userID) ??
        "")
      : "";
  return {
    id: item.id,
    educationCode: item.code ?? "",
    educationName: item.name ?? "",
    educatorName,
    format: apiTypeToFormat(item.type),
    targetAudience: item.scopeType as 0 | 1 | 2,
    registrationCount: item.totalEnrolledCount ?? 0,
    maxRegistration: item.capacity,
    publishDate: item.startDate ?? item.createdAt ?? "",
    status: apiStatusToStatus(item.status),
    isRequired: item.isRequired === true,
    isCurrentUserManager: item.isCurrentUserManager === true,
    createdAt: item.createdAt ?? "",
    updatedAt: item.updatedAt ?? "",
  };
}

// Mock data for development/demo purposes
// Replace this with actual API calls in production
const MOCK_EDUCATIONS: Education[] = [
  {
    id: "1",
    educationCode: "ETK-2025-014",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "uniteli",
    targetAudience: 1,
    registrationCount: 450,
    publishDate: "2026-01-12",
    status: "aktif",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "2",
    educationCode: "INV-24321",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "uniteli",
    targetAudience: 0,
    registrationCount: 243,
    publishDate: "2026-01-12",
    status: "talepToplama",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "3",
    educationCode: "INV-24321",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "uniteli",
    targetAudience: 0,
    registrationCount: 344,
    publishDate: "2026-01-12",
    status: "kapali",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "4",
    educationCode: "INV-24321",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "canli",
    targetAudience: 2,
    registrationCount: 120,
    maxRegistration: 200,
    publishDate: "2026-01-12",
    status: "butceBekliyor",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "5",
    educationCode: "INV-24321",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "canli",
    targetAudience: 0,
    registrationCount: 120,
    maxRegistration: 200,
    publishDate: "2026-01-12",
    status: "aktif",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "6",
    educationCode: "INV-24321",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "canli",
    targetAudience: 1,
    registrationCount: 60,
    maxRegistration: 100,
    publishDate: "2026-01-12",
    status: "tamamlandi",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "7",
    educationCode: "INV-24321",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "yuzYuze",
    targetAudience: 2,
    registrationCount: 60,
    maxRegistration: 100,
    publishDate: "2026-01-12",
    status: "butceBekliyor",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "8",
    educationCode: "INV-24321",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "canli",
    targetAudience: 0,
    registrationCount: 450,
    maxRegistration: 500,
    publishDate: "2026-01-12",
    status: "taslak",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "9",
    educationCode: "INV-24321",
    educationName: i18n.t("mockData.education.osgBasic"),
    educatorName: "Ayşe Yılmaz",
    format: "canli",
    targetAudience: 1,
    registrationCount: 450,
    maxRegistration: 500,
    publishDate: "2026-01-12",
    status: "taslak",
    createdAt: "2026-01-01T00:00:00.000Z",
    updatedAt: "2026-01-01T00:00:00.000Z",
  },
  {
    id: "10",
    educationCode: "INV-24322",
    educationName: i18n.t("mockData.education.projectManagement"),
    educatorName: "Mehmet Demir",
    format: "yuzYuze",
    targetAudience: 0,
    registrationCount: 85,
    maxRegistration: 100,
    publishDate: "2026-01-15",
    status: "aktif",
    createdAt: "2026-01-02T00:00:00.000Z",
    updatedAt: "2026-01-02T00:00:00.000Z",
  },
  {
    id: "11",
    educationCode: "ETK-2025-015",
    educationName: i18n.t("mockData.education.reactWeb"),
    educatorName: "Mehmet Demir",
    format: "canli",
    targetAudience: 0,
    registrationCount: 180,
    maxRegistration: 200,
    publishDate: "2026-01-10",
    status: "aktif",
    createdAt: "2026-01-02T00:00:00.000Z",
    updatedAt: "2026-01-02T00:00:00.000Z",
  },
  {
    id: "12",
    educationCode: "ETK-2025-016",
    educationName: i18n.t("mockData.education.projectManagementBasics"),
    educatorName: "Fatma Kaya",
    format: "yuzYuze",
    targetAudience: 2,
    registrationCount: 45,
    maxRegistration: 50,
    publishDate: "2026-01-08",
    status: "tamamlandi",
    createdAt: "2026-01-03T00:00:00.000Z",
    updatedAt: "2026-01-03T00:00:00.000Z",
  },
  {
    id: "13",
    educationCode: "ETK-2025-017",
    educationName: "Finansal Analiz ve Raporlama",
    educatorName: "Ali Öztürk",
    format: "uniteli",
    targetAudience: 0,
    registrationCount: 320,
    publishDate: "2026-01-05",
    status: "aktif",
    createdAt: "2026-01-04T00:00:00.000Z",
    updatedAt: "2026-01-04T00:00:00.000Z",
  },
  {
    id: "14",
    educationCode: "ETK-2025-018",
    educationName: "Python ile Veri Analizi",
    educatorName: "Selin Yıldız",
    format: "canli",
    targetAudience: 1,
    registrationCount: 95,
    maxRegistration: 150,
    publishDate: "2026-01-14",
    status: "talepToplama",
    createdAt: "2026-01-05T00:00:00.000Z",
    updatedAt: "2026-01-05T00:00:00.000Z",
  },
  {
    id: "15",
    educationCode: "ETK-2025-019",
    educationName: i18n.t("mockData.education.iso9001"),
    educatorName: "Emre Koç",
    format: "uniteli",
    targetAudience: 2,
    registrationCount: 275,
    publishDate: "2026-01-11",
    status: "butceBekliyor",
    createdAt: "2026-01-06T00:00:00.000Z",
    updatedAt: "2026-01-06T00:00:00.000Z",
  },
  {
    id: "16",
    educationCode: "ETK-2025-020",
    educationName: i18n.t("mockData.education.electricalSafety"),
    educatorName: "Hakan Şahin",
    format: "yuzYuze",
    targetAudience: 1,
    registrationCount: 28,
    maxRegistration: 30,
    publishDate: "2026-01-09",
    status: "kapali",
    createdAt: "2026-01-07T00:00:00.000Z",
    updatedAt: "2026-01-07T00:00:00.000Z",
  },
  {
    id: "17",
    educationCode: "ETK-2025-021",
    educationName: i18n.t("mockData.education.communicationPresentation"),
    educatorName: "Burak Çelik",
    format: "canli",
    targetAudience: 0,
    registrationCount: 110,
    maxRegistration: 120,
    publishDate: "2026-01-13",
    status: "butceBekliyor",
    createdAt: "2026-01-08T00:00:00.000Z",
    updatedAt: "2026-01-08T00:00:00.000Z",
  },
  {
    id: "18",
    educationCode: "ETK-2025-022",
    educationName: i18n.t("mockData.education.technicalDrawingCad"),
    educatorName: "Zeynep Arslan",
    format: "yuzYuze",
    targetAudience: 2,
    registrationCount: 22,
    maxRegistration: 25,
    publishDate: "2026-01-07",
    status: "aktif",
    createdAt: "2026-01-09T00:00:00.000Z",
    updatedAt: "2026-01-09T00:00:00.000Z",
  },
  {
    id: "19",
    educationCode: "ETK-2025-023",
    educationName: i18n.t("mockData.education.accountingTax"),
    educatorName: "Deniz Aydın",
    format: "uniteli",
    targetAudience: 0,
    registrationCount: 410,
    publishDate: "2026-01-06",
    status: "tamamlandi",
    createdAt: "2026-01-10T00:00:00.000Z",
    updatedAt: "2026-01-10T00:00:00.000Z",
  },
  {
    id: "20",
    educationCode: "ETK-2025-024",
    educationName: i18n.t("mockData.education.cyberSecurity"),
    educatorName: "Can Yılmaz",
    format: "canli",
    targetAudience: 2,
    registrationCount: 200,
    maxRegistration: 300,
    publishDate: "2026-01-15",
    status: "taslak",
    createdAt: "2026-01-11T00:00:00.000Z",
    updatedAt: "2026-01-11T00:00:00.000Z",
  },
];

/** Map UI status to API status number */
function statusToApi(status: EducationStatus): number | undefined {
  const map: Record<EducationStatus, number> = {
    talepToplama: EDUCATION_STATUS_API.REQUEST_COLLECTION,
    butceBekliyor: EDUCATION_STATUS_API.BUDGET_PENDING,
    aktif: EDUCATION_STATUS_API.ACTIVE,
    kapali: EDUCATION_STATUS_API.CLOSED,
    tamamlandi: EDUCATION_STATUS_API.ARCHIVED,
    taslak: EDUCATION_STATUS_API.DRAFT,
  };
  return map[status];
}

/** Map UI format to API type number */
function formatToApiType(format: EducationFormat): number | undefined {
  const map: Record<EducationFormat, number> = {
    yuzYuze: EDUCATION_TYPE_API.ON_SITE,
    canli: EDUCATION_TYPE_API.ONLINE,
    uniteli: EDUCATION_TYPE_API.UNIT,
    nonUnit: EDUCATION_TYPE_API.NONUNIT,
  };
  return map[format];
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

// In-memory storage for mock data (used by getById/create/update/delete until those endpoints exist)
const educationsData = [...MOCK_EDUCATIONS];

export const educationManagementApi = {
  /**
   * POST /api/admin/locations — Yeni hazır konum oluşturur.
   * Yetki: LOCATIONS.CREATE
   */
  createLocation: async (body: {
    name: string;
    address?: string;
    latitude?: number;
    longitude?: number;
  }): Promise<{
    id: string;
    name: string;
    address?: string;
    latitude?: number | null;
    longitude?: number | null;
  }> => {
    const { data } = await apiClient.post<
      | {
          id: string;
          name: string;
          address?: string;
          latitude?: number | null;
          longitude?: number | null;
        }
      | {
          data: {
            id: string;
            name: string;
            address?: string;
            latitude?: number | null;
            longitude?: number | null;
          };
        }
    >(ADMIN_LOCATIONS_ENDPOINT, body);
    const entity = (data as { data?: unknown })?.data ?? data;
    return entity as {
      id: string;
      name: string;
      address?: string;
      latitude?: number | null;
      longitude?: number | null;
    };
  },

  /** PATCH /api/admin/locations/:id */
  updateLocation: async (
    id: string,
    body: {
      name?: string;
      address?: string;
      latitude?: number;
      longitude?: number;
    },
  ): Promise<{
    id: string;
    name: string;
    address?: string;
    latitude?: number | null;
    longitude?: number | null;
  }> => {
    const { data } = await apiClient.patch(`${ADMIN_LOCATIONS_ENDPOINT}/${id}`, body);
    const entity = (data as { data?: unknown })?.data ?? data;
    return entity as {
      id: string;
      name: string;
      address?: string;
      latitude?: number | null;
      longitude?: number | null;
    };
  },

  /** DELETE /api/admin/locations/:id */
  deleteLocation: async (id: string): Promise<void> => {
    await apiClient.delete(`${ADMIN_LOCATIONS_ENDPOINT}/${id}`);
  },

  getLocations: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    sortBy?: string;
    sortOrder?: "asc" | "desc";
  }): Promise<PresetLocationsResponseApi> => {
    const { data } = await apiClient.get<PresetLocationsResponseApi>(
      ADMIN_LOCATIONS_ENDPOINT,
      {
        params: {
          page: params?.page ?? 1,
          limit: params?.limit ?? 50,
          search: params?.search ?? "",
          sortBy: params?.sortBy ?? "createdAt",
          sortOrder: params?.sortOrder ?? "desc",
        },
      },
    );
    return data;
  },
  /**
   * Get paginated list of educations with filters (GET /api/admin/educations)
   */
  getAll: async (params?: {
    page?: number;
    pageSize?: number;
    filters?: EducationFilters;
    sortBy?: EducationListParams["sortBy"];
    sortOrder?: EducationListParams["sortOrder"];
  }) => {
    const page = params?.page ?? 1;
    const limit = params?.pageSize ?? 10;
    const filters = params?.filters ?? {};

    const apiParams: EducationListParams = {
      page,
      limit,
      search: filters.search || undefined,
      sortBy: params?.sortBy ?? "createdAt",
      sortOrder: params?.sortOrder ?? "desc",
    };

    // GET: type (0-3) — only set when we have a valid number (dropdown clear can send "" → parseInt → NaN)
    if (filters.type !== undefined) {
      const val = Array.isArray(filters.type) ? filters.type[0] : filters.type;
      const num =
        val !== undefined && val !== null
          ? typeof val === "number"
            ? val
            : parseInt(String(val), 10)
          : NaN;
      if (!Number.isNaN(num)) apiParams.type = num;
    } else if (filters.format !== undefined) {
      const val = Array.isArray(filters.format)
        ? filters.format[0]
        : filters.format;
      if (val !== undefined && val !== null) {
        const num =
          typeof val === "number"
            ? val
            : formatToApiType(val as EducationFormat);
        if (!Number.isNaN(num)) apiParams.type = num;
      }
    }
    // GET: status (0-4). `statusIn` array olarak gönderiyoruz; tek değer 0
    // (REQUEST_COLLECTION) olduğunda axios paramsSerializer'ın falsy edge-case
    // davranışına düşmemek için query `statusIn=0` formunda gider.
    if (filters.status !== undefined) {
      const rawValues = Array.isArray(filters.status)
        ? filters.status
        : [filters.status];
      const nums: number[] = [];
      for (const val of rawValues) {
        if (val === undefined || val === null || (val as unknown) === "") continue;
        const n =
          typeof val === "number"
            ? val
            : Number.isNaN(Number(val))
              ? statusToApi(val as EducationStatus)
              : parseInt(String(val), 10);
        if (n !== undefined && !Number.isNaN(n)) nums.push(n);
      }
      if (nums.length > 0) {
        apiParams.statusIn = nums;
        // Bazı backend endpoint sürümleri statusIn=[0] sorgusunu yanlış
        // yorumluyor (talep toplama yerine aktif eğitim döndürüyor). Tek
        // değerli filtrelerde aynı bilgiyi `status` olarak da yollayıp
        // tutarsızlığı önlüyoruz.
        if (nums.length === 1) {
          (apiParams as { status?: number }).status = nums[0];
        }
      }
    }
    if (filters.targetAudience !== undefined) {
      const val = Array.isArray(filters.targetAudience)
        ? filters.targetAudience[0]
        : filters.targetAudience;
      if (typeof val === "number") apiParams.scopeType = val;
    }
    // GET: categoryIds, instructorIds
    if (filters.categoryIds?.length)
      apiParams.categoryIds = filters.categoryIds;
    if (filters.instructorIds?.length)
      apiParams.instructorIds = filters.instructorIds;
    // Yayınlanma Tarihi aralığı backend'e startDateFrom / startDateTo olarak gönderilir (YYYY-MM-DD, yerel tarih)
    const pub = filters.publishDate;
    if (Array.isArray(pub)) {
      const toYmd = (v: Date | string | null): string => {
        if (v == null) return "";
        if (v instanceof Date) {
          const y = v.getFullYear();
          const m = String(v.getMonth() + 1).padStart(2, "0");
          const d = String(v.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        }
        const s = typeof v === "string" ? v.trim() : "";
        return s;
      };
      const from = toYmd(pub[0]);
      const to = toYmd(pub[1]);
      if (from) apiParams.startDateFrom = from;
      if (to) apiParams.startDateTo = to;
    }

    // Zorunlu eğitim filtresi — dropdown "true" / "false" döner
    if (filters.isRequired !== undefined && filters.isRequired !== "") {
      const raw = Array.isArray(filters.isRequired)
        ? filters.isRequired[0]
        : filters.isRequired;
      if (raw === true || raw === "true") apiParams.isRequired = true;
      else if (raw === false || raw === "false") apiParams.isRequired = false;
    }

    const { data: response } = await apiClient.get<EducationListResponseApi>(
      ADMIN_EDUCATIONS_ENDPOINT,
      { params: apiParams },
    );

    const items = Array.isArray(response?.data) ? response.data : [];
    const total = response?.count ?? 0;

    return {
      data: items.map(mapApiItemToEducation),
      total,
      page,
      pageSize: limit,
    };
  },

  /**
   * Get education categories for filter dropdown (GET /api/client/education-categories)
   */
  getCategories: async (): Promise<{ id: string; name?: string }[]> => {
    const response = await apiClient.get(EDUCATION_CATEGORIES_ENDPOINT);
    const data = response.data?.data ?? response.data?.items ?? response.data;
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get instructors for filter dropdown (GET /api/users/instructors)
   */
  getInstructors: async (): Promise<
    { id: string; userID?: string; firstName?: string; lastName?: string }[]
  > => {
    const response = await apiClient.get(USERS_INSTRUCTORS_ENDPOINT);
    const data = response.data?.items ?? response.data?.data ?? response.data;
    return Array.isArray(data) ? data : [];
  },

  /**
   * Get education by ID (GET /api/admin/educations/{id})
   */
  getById: async (id: string): Promise<{ data: EducationDetailApi }> => {
    const { data: response } = await apiClient.get<EducationDetailResponseApi>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${id}`,
    );
    if (!response?.data) {
      throw new Error("Education not found");
    }
    return { data: response.data };
  },

  /**
   * GET /api/admin/educations/{id}/mandatory/health
   * Zorunlu eğitim atama sağlık durumu — expected vs actual enrollment
   * sayısı, son reconcile zamanı. `isHealthy=false` ise admin UI banner açıp
   * "Atamayı Yeniden Çalıştır" butonunu gösterir.
   */
  getMandatoryHealth: async (
    id: string,
  ): Promise<MandatoryHealthResponse> => {
    const { data } = await apiClient.get<{ data?: MandatoryHealthResponse } | MandatoryHealthResponse>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${id}/mandatory/health`,
    );
    const payload =
      (data as { data?: MandatoryHealthResponse })?.data ??
      (data as MandatoryHealthResponse);
    return payload;
  },

  /**
   * GET /api/admin/educations/{id}/mandatory/missing-users
   * Hedef kitledeki kullanıcılardan bu eğitime atanmamış olanları döner.
   */
  getMandatoryMissingUsers: async (
    id: string,
  ): Promise<{
    users: Array<{
      id: string;
      firstName: string | null;
      lastName: string | null;
      email: string | null;
      phoneNumber: string | null;
      departmentID: string | null;
      departmentName: string | null;
    }>;
    count: number;
  }> => {
    const { data } = await apiClient.get<{
      data?: {
        users: Array<{
          id: string;
          firstName: string | null;
          lastName: string | null;
          email: string | null;
          phoneNumber: string | null;
          departmentID: string | null;
          departmentName: string | null;
        }>;
        count: number;
      };
    }>(`${ADMIN_EDUCATIONS_ENDPOINT}/${id}/mandatory/missing-users`);
    const payload = (data as any)?.data ?? (data as any);
    return {
      users: Array.isArray(payload?.users) ? payload.users : [],
      count: Number(payload?.count ?? 0),
    };
  },

  /**
   * POST /api/admin/educations/{id}/mandatory/reconcile
   * Zorunlu eğitim atama kuyruğuna yeniden job atar. Idempotent — BullMQ
   * aynı jobId'yi reddettiği için birden fazla tıklama tek worker koşusuna düşer.
   */
  reconcileMandatory: async (id: string): Promise<{ success: boolean }> => {
    const { data } = await apiClient.post<{ success?: boolean }>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${id}/mandatory/reconcile`,
      {},
    );
    return { success: data?.success ?? true };
  },

  /**
   * Create approved enrollment (POST /api/admin/educations/{educationId}/enrollments)
   * Admin-only. Enrollment is set to ACTIVE.
   */
  createEnrollment: async (
    educationId: string,
    body: { userId: string; reviewerUserId: string },
  ) => {
    const { data } = await apiClient.post<{ data?: unknown }>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/enrollments`,
      body,
    );
    return data;
  },

  /**
   * Şablon kodundan prefill verisi getir (GET /api/admin/educations/template-lookup).
   * Eşleşme yoksa null döner. Frontend bu verilerle form'u prefill eder.
   */
  lookupTemplate: async (
    templateCode: string,
  ): Promise<null | {
    name: string;
    description: string | null;
    content: string | null;
    durationMinutes: number;
    type: number;
    scopeType: number;
    categoryIDs: string[];
  }> => {
    const { data } = await apiClient.get<{
      data?: {
        found: boolean;
        prefill?: {
          name: string;
          description: string | null;
          content: string | null;
          durationMinutes: number;
          type: number;
          scopeType: number;
          categoryIDs: string[];
        };
      };
    }>(`${ADMIN_EDUCATIONS_ENDPOINT}/template-lookup`, { params: { templateCode } });
    const payload = data?.data;
    return payload?.found && payload.prefill ? payload.prefill : null;
  },

  /**
   * Create new education (POST /api/admin/educations)
   */
  create: async (data: AdminCreateEducationDto) => {
    const { data: response } = await apiClient.post<{
      data: { id: string };
      [key: string]: unknown;
    }>(ADMIN_EDUCATIONS_ENDPOINT, data);
    const id = response?.data?.id ?? (response as { id?: string })?.id;
    if (!id) throw new Error("Create education response missing id");
    return { data: { id } };
  },

  /**
   * Update education (mock – use patch for API)
   */
  update: async (id: string, data: Partial<CreateEducationRequest>) => {
    await delay(500);
    const itemIndex = educationsData.findIndex((item) => item.id === id);
    if (itemIndex === -1) {
      throw new Error("Education not found");
    }
    educationsData[itemIndex] = {
      ...educationsData[itemIndex],
      ...data,
      updatedAt: new Date().toISOString(),
    };
    return { data: educationsData[itemIndex] };
  },

  /**
   * Patch education (PATCH /api/admin/educations/{id})
   * chapters gönderilirse mevcut bölümler soft-delete, gönderilen liste yeni bölümler olarak oluşturulur.
   */
  patch: async (id: string, data: AdminPatchEducationDto) => {
    const { data: response } = await apiClient.patch<{
      data: { id: string };
      [key: string]: unknown;
    }>(`${ADMIN_EDUCATIONS_ENDPOINT}/${id}`, data);
    return { data: response?.data ?? { id } };
  },

  /**
   * Delete education (DELETE /api/admin/educations/{id})
   */
  delete: async (id: string) => {
    await apiClient.delete(`${ADMIN_EDUCATIONS_ENDPOINT}/${id}`);
    return { data: { id } };
  },

  /**
   * Get attendance list for an education by date (GET /api/educations/{educationId}/attendance)
   * Instructor only. Only ONLINE and ON_SITE.
   */
  getAttendance: async (educationId: string, params: GetAttendanceParams) => {
    const { data } = await apiClient.get<{ data: AttendanceListResponseApi }>(
      `${EDUCATIONS_ATTENDANCE_ENDPOINT}/${educationId}/attendance`,
      { params: { ...params, attendanceDate: params.attendanceDate } },
    );
    return {
      data: data?.data ?? {
        attendanceDate: params.attendanceDate,
        items: [],
        total: 0,
        page: params.page ?? 1,
        limit: params.limit ?? 10,
      },
    };
  },

  /**
   * Put batch attendance (PUT /api/educations/{educationId}/attendance)
   * Idempotent. Sets attendance for multiple enrollments for a single date.
   */
  putAttendance: async (educationId: string, body: PutAttendanceBody) => {
    const { data } = await apiClient.put<{
      success: boolean;
      data?: { updated?: number };
      message?: string;
    }>(`${EDUCATIONS_ATTENDANCE_ENDPOINT}/${educationId}/attendance`, body);
    return data;
  },

  /**
   * Eğitim tamamlama diyaloğu için aday katılımcılar
   * (GET /api/admin/educations/{id}/complete-candidates)
   */
  getCompleteCandidates: async (
    educationId: string,
  ): Promise<CompleteEducationCandidatesResponse> => {
    const { data: response } = await apiClient.get<{
      data: CompleteEducationCandidatesResponse;
    }>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/complete-candidates`,
    );
    return (
      response?.data ??
      ({ educationType: 0, candidates: [], groups: [] } as CompleteEducationCandidatesResponse)
    );
  },

  /**
   * Eğitimi tamamla — kullanıcı başına karar ile
   * (PATCH /api/admin/educations/{id}/complete)
   *
   * Body zorunlu: aktif katılımcıların her biri için COMPLETED / NOT_COMPLETED / BLACKLISTED.
   * En az bir BLACKLISTED varsa ortak blacklist bilgisi (reason/startDate/endDate/description) gereklidir.
   */
  completeEducation: async (
    educationId: string,
    body: CompleteEducationRequest,
  ): Promise<CompleteEducationResponse> => {
    const { data } = await apiClient.patch<CompleteEducationResponse>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/complete`,
      body,
    );
    return data;
  },

  /**
   * Eğitimi kapatmak için açık enrollment özeti
   * (GET /api/admin/educations/{id}/pending-close-summary)
   */
  getPendingCloseSummary: async (
    educationId: string,
  ): Promise<{
    pending: Array<{
      enrollmentId: string;
      userId: string;
      firstName: string;
      lastName: string;
      status: number;
      virtualClassroomId: string | null;
      virtualClassroomName: string | null;
    }>;
  }> => {
    const { data: response } = await apiClient.get<{
      data?: {
        pending?: Array<{
          enrollmentId: string;
          userId: string;
          firstName: string;
          lastName: string;
          status: number;
          virtualClassroomId: string | null;
          virtualClassroomName: string | null;
        }>;
      };
    }>(`${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/pending-close-summary`);
    return { pending: response?.data?.pending ?? [] };
  },

  /**
   * Eğitimi kapat — sene sonu manuel
   * (PATCH /api/admin/educations/{id}/close)
   *
   * - `decisions` opsiyonel: açık enrollment'lar için karar.
   * - `forceCloseAsNotCompleted=true`: karar verilmemiş açıkları otomatik
   *   NOT_COMPLETED'a alır.
   * - false ve eksik karar varsa 409 döner; UI uyarı modal'ında pending listesi
   *   yeniden çekilir.
   */
  closeEducation: async (
    educationId: string,
    body: {
      decisions?: CompleteEducationRequest["decisions"];
      blacklist?: CompleteEducationRequest["blacklist"];
      forceCloseAsNotCompleted?: boolean;
    },
  ): Promise<CompleteEducationResponse> => {
    const { data } = await apiClient.patch<CompleteEducationResponse>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/close`,
      body,
    );
    return data;
  },

  /**
   * Yeni dönem başlat (POST /api/admin/educations/{id}/new-period)
   * Sadece EDUCATION_MANAGER. Eğitim CLOSED durumunda olmalı.
   */
  startNewPeriod: async (
    educationId: string,
  ): Promise<{ educationID: string; offeringID: string; sequenceNumber: number }> => {
    const { data } = await apiClient.post<{
      educationID: string;
      offeringID: string;
      sequenceNumber: number;
    }>(`${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/new-period`);
    return data;
  },

  /**
   * Kayıtlı katılımcıları dışa aktar (GET /api/admin/educations/{educationId}/enrolled-participants/export)
   */
  exportEnrolledParticipants: async (
    educationId: string,
  ): Promise<{ blob: Blob; filename: string }> => {
    const res = await apiClient.get<Blob>(
      `${ADMIN_EDUCATIONS_ENDPOINT}/${educationId}/enrolled-participants/export`,
      { responseType: "blob" },
    );
    const header = res.headers["content-disposition"] as string | undefined;
    let filename = `katilimcilar-${educationId}.xlsx`;
    if (header) {
      const utf8 = /filename\*=UTF-8''([^;\n]+)/i.exec(header);
      if (utf8) {
        filename = decodeURIComponent(utf8[1].trim());
      } else {
        const quoted = /filename="([^"]+)"/i.exec(header);
        if (quoted) filename = quoted[1];
        else {
          const loose = /filename=([^;\n]+)/i.exec(header);
          if (loose) filename = loose[1].replace(/"/g, "").trim();
        }
      }
    }
    return { blob: res.data as Blob, filename };
  },
};

// For production, use actual API calls:
// export const educationManagementApi = {
//   getAll: (params?: Record<string, any>) =>
//     apiClient.get<Education[]>("/api/educations", { params }),
//   getById: (id: string) => apiClient.get<Education>(`/api/educations/${id}`),
//   create: (data: CreateEducationRequest) => apiClient.post<Education>("/api/educations", data),
//   update: (id: string, data: Partial<CreateEducationRequest>) =>
//     apiClient.put<Education>(`/api/educations/${id}`, data),
//   delete: (id: string) => apiClient.delete(`/api/educations/${id}`),
// }
